import React, { useState, useEffect, useCallback } from "react";
import { auth, db } from "../firebase";
import { doc, getDoc, setDoc, collection, getDocs } from "firebase/firestore";
import { useNavigate, useLocation } from "react-router-dom";
import CryptoJS from "crypto-js";
import { Router, Route, Routes } from 'react-router-dom';
import {
  Typography,
  Button,
  Card,
  CardContent,
  Container,
  Grid2 as Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Box,
  Fade,
  LinearProgress,
} from "@mui/material";
import { styled } from "@mui/system";
import { onAuthStateChanged } from "firebase/auth";
import MathGemLogoMain from "../assets/MathGemLogoMain.png";


const CYBERSOURCE_CONFIG = {
  ACCESS_KEY: '0105d945f85c368cbec7459cc6d30465',
  PROFILE_ID: '14098329-8313-4F96-909A-3C957A276641',
  SECRET_KEY: '2052d69cb9794c9991ed44a1efa528a394f84c9653ec4077af586faa177591fd71c7a66e055a4371bc163c6e80ebd36f8339a7f784f7478eac4dc810b0531459f751b7399aa84a649c9430b0f5e89c2f86dd0fa12299406fbadd2e9e7b8823d7f0ada056879a459eaf110d019f69e36a1754fc747bc042b7b4122f08f390624b',
  URL: 'https://secureacceptance.cybersource.com/pay',
};

// Subscription plans
const PLANS = [
  { name: "Monthly", price: 300, display: "Monthly - Rs300", amount: "300.00", period: "monthly" },
  { name: "Termly", price: 750, display: "Termly - Rs300", amount: "750.00", period: "termly" },
  { name: "Yearly", price: 3000, display: "Yearly - Rs3000", amount: "3000.00", period: "yearly" },
];

// Styled components
const GradientHeader = styled(Box)(({ theme }) => ({
  background: "linear-gradient(45deg, #4caf50 30%, #81c784 90%)",
  borderRadius: "12px 12px 0 0",
  padding: theme.spacing(4),
  textAlign: "center",
  color: "white",
  marginBottom: theme.spacing(4),
}));

const colors = {
  primary: '#2ecc71',
  secondary: '#34495e',
  accent: '#3498db',
  background: '#f5f7fa',
  gradientStart: 'rgba(62,180,137,1)',
  gradientEnd: 'rgba(144,238,144,1)',
};

const PlanCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: "0 6px 24px rgba(0, 0, 0, 0.15)",
  textAlign: "center",
  padding: theme.spacing(4),
  background: "linear-gradient(135deg, #ffffff 0%, #f5f9fc 100%)",
  transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: "0 12px 32px rgba(0, 0, 0, 0.2)",
  },
}));

const SelectButton = styled(Button)(({ theme }) => ({
  borderRadius: 25,
  padding: theme.spacing(1.5, 5),
  background: "linear-gradient(45deg, #4caf50 30%, #81c784 90%)",
  color: "white",
  textTransform: "none",
  fontWeight: 700,
  fontSize: "1rem",
  "&:hover": {
    background: "linear-gradient(45deg, #45a049 30%, #75b377 90%)",
    boxShadow: "0 4px 12px rgba(76, 175, 80, 0.3)",
  },
  transition: "all 0.3s ease-in-out",
}));

const ModernDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: 16,
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
    background: "linear-gradient(135deg, #ffffff 0%, #f0f4f8 100%)",
    border: "1px solid rgba(173, 223, 173, 0.3)",
    overflow: "hidden",
    width: "100%",
    maxWidth: 500,
  },
}));

const ModernDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(4),
  textAlign: "left",
}));

const ModernTable = styled(TableContainer)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
  "& .MuiTableRow-root:nth-of-type(odd)": {
    backgroundColor: "#f9fafb",
  },
  "& .MuiTableRow-root:hover": {
    backgroundColor: "#e8f5e9",
  },
}));

const Subscription = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();

  // Check authentication status
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await fetchPayments(currentUser.uid);
      } else {
        navigate("/sign-in");
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [navigate]);

  // Fetch payment history from Firestore
  const fetchPayments = async (userId) => {
    try {
      const paymentsCollection = collection(db, `users/${userId}/payments`);
      const paymentSnapshot = await getDocs(paymentsCollection);
      const paymentList = paymentSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setPayments(paymentList);
    } catch (err) {
      console.error("Error fetching payments:", err);
      setError("Failed to load payment history. Please try again.");
    }
  };

  // Generate payment data and signature
  const generatePaymentData = useCallback(
    (plan) => {
      const transactionUuid = Math.random().toString(36).substring(2, 15);
      const signedDateTime = new Date().toISOString().replace(/\.[0-9]{3}/, '');
      const signedFieldNames =
        'access_key,profile_id,transaction_uuid,signed_date_time,transaction_type,reference_number,amount,currency,locale,signed_field_names,bill_to_email,bill_to_forename,bill_to_surname,override_custom_receipt_page';
      const referenceNumber = `SUB_${user?.uid}_${Date.now()}`;
      const data = {
        access_key: CYBERSOURCE_CONFIG.ACCESS_KEY,
        profile_id: CYBERSOURCE_CONFIG.PROFILE_ID,
        transaction_uuid: transactionUuid,
        signed_date_time: signedDateTime,
        transaction_type: 'sale',
        reference_number: referenceNumber,
        amount: plan.amount,
        currency: 'SCR',
        locale: 'en',
        signed_field_names: signedFieldNames,
        bill_to_email: user?.email || '',
        bill_to_forename: user?.displayName?.split(' ')[0] || 'User',
        bill_to_surname: user?.displayName?.split(' ')[1] || 'Unknown',
        override_custom_receipt_page: `${window.location.origin}/response`,
        signature: '',
      };

      const dataString = Object.keys(data)
        .filter((key) => key !== 'signature')
        .map((key) => `${key}=${data[key]}`)
        .join(',');
      data.signature = CryptoJS.HmacSHA256(dataString, CYBERSOURCE_CONFIG.SECRET_KEY).toString(
        CryptoJS.enc.Base64
      );

      return data;
    },
    [user]
  );

  // Handle plan selection
  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setOpenDialog(true);
  };

  // Handle payment submission
  const handleConfirmPayment = async () => {
    if (!selectedPlan) {
      setError("Please select a plan.");
      return;
    }

    const plan = PLANS.find((p) => p.name === selectedPlan.name);
    if (!plan) {
      setError("Invalid plan selected.");
      return;
    }

    setIsProcessing(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = generatePaymentData(plan);
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = CYBERSOURCE_CONFIG.URL;

      Object.entries(formData).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value;
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
    } catch (err) {
      console.error("Payment initiation error:", err);
      setError("Failed to initiate payment. Please try again.");
      setIsProcessing(false);
    }
  };

  // Handle dialog close
  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedPlan(null);
  };

  // Handle Snackbar close
  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setError(null);
    setSuccess(null);
  };

  if (loading) {
    return (
      <div style={{ background: '#f5f7fa' }}>
        <Container
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            background: colors.background,
          }}
        >
          <img src={MathGemLogoMain} width={350} alt="Math Gem Logo" />
          <Fade in={true} timeout={600}>
            <LinearProgress
              sx={{ color: colors.primary, mb: 2, mt: 5, width: '50%' }}
              size={60}
              thickness={4}
            />
          </Fade>
          <Typography variant="hRosemary" sx={{ color: colors.secondary }}>
            Loading Subscriptions...
          </Typography>
        </Container>
      </div>
    );
  }

  return (
    <Container sx={{ mt: 10, mb: 10 }}>
      <Paper sx={{ borderRadius: 5, overflow: "hidden" }} elevation={0}>
        <Grid container spacing={0}>
          {/* Subscription Plans */}
          <Grid size={{ xs: 12, md: 12 }}>
            <Box sx={{ p: { xs: 3, md: 5 } }}>
              <Typography
                variant="h4"
                sx={{ fontWeight: 800, mb: 3, color: "#2e7d32", textAlign: "center" }}
              >
                Choose Your Subscription
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ mb: 4, textAlign: "center", color: "#424242" }}
              >
                Unlock premium features with a Monthly or Yearly plan.
              </Typography>
              <Grid container spacing={3} justifyContent="center">
                {PLANS.map((plan) => (
                  <Grid key={plan.name} size={{ xs: 12, sm: 6 }}>
                    <PlanCard>
                      <CardContent>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: "#2e7d32" }}>
                          {plan.name} Plan
                        </Typography>
                        <Typography
                          variant="h6"
                          color="text.secondary"
                          sx={{ my: 2, fontWeight: 500 }}
                        >
                          {plan.display}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3, color: "#616161" }}>
                          {plan.name === "Monthly"
                            ? "Access all premium features for 30 days."
                            : "Full-year access and save on premium features!"}
                        </Typography>
                        <SelectButton onClick={() => handleSelectPlan(plan)}>
                          Select Plan
                        </SelectButton>
                      </CardContent>
                    </PlanCard>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>
        </Grid>

        {/* Payment History */}
        <Box sx={{ p: { xs: 3, md: 5 } }}>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, mb: 4, color: "#2e7d32", textAlign: "center" }}
          >
            Payment History
          </Typography>
          {payments.length === 0 ? (
            <Typography variant="body1" align="center" color="text.secondary">
              No payments found.
            </Typography>
          ) : (
            <ModernTable>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Plan</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Invoice</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell>{new Date(payment.date).toLocaleDateString()}</TableCell>
                      <TableCell>{payment.plan}</TableCell>
                      <TableCell>Rs{payment.amount}</TableCell>
                      <TableCell>{payment.status}</TableCell>
                      <TableCell>
                        <Button
                          variant="text"
                          color="primary"
                          href={`#invoice-${payment.invoiceId}`}
                          onClick={() => alert("Invoice download not implemented yet.")}
                          sx={{ textTransform: "none" }}
                        >
                          View Invoice
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ModernTable>
          )}
        </Box>
      </Paper>

      {/* Confirmation Dialog */}
      <ModernDialog open={openDialog} onClose={handleDialogClose}>
        <DialogTitle sx={{ background: "#4caf50", color: "white", fontWeight: 700 }}>
          Confirm Subscription
        </DialogTitle>
        <ModernDialogContent>
          <Typography sx={{ mb: 2 }}>
            You have selected the {selectedPlan?.name} plan (Rs{selectedPlan?.price}).
            You will be redirected to a secure payment page to complete your subscription.
          </Typography>
        </ModernDialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={handleDialogClose}
            disabled={isProcessing}
            sx={{ textTransform: "none", color: "#616161" }}
          >
            Cancel
          </Button>
          <SelectButton
            onClick={handleConfirmPayment}
            disabled={isProcessing}
            startIcon={isProcessing ? <CircularProgress size={20} /> : null}
          >
            Proceed to Payment
          </SelectButton>
        </DialogActions>
      </ModernDialog>

      {/* Snackbar for messages */}
      <Snackbar
        open={!!error || !!success}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={error ? "error" : "success"}
          sx={{ width: "100%" }}
        >
          {error || success}
        </Alert>
      </Snackbar>
    </Container>
  );
};

// Response handling component
const ResponsePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const decision = queryParams.get('decision') || 'unknown';
  const transactionId = queryParams.get('transaction_id') || 'N/A';
  const referenceNumber = queryParams.get('req_reference_number') || 'N/A';
  const reasonCode = queryParams.get('reason_code') || 'unknown';
  const message = queryParams.get('message') || 'Unknown error';

  // State for modal
  const [modal, setModal] = useState({
    visible: true,
    message: decision === 'ACCEPT' ? 'Payment Successful!' : `Payment Failed: ${message}`,
    isSuccess: decision === 'ACCEPT',
  });

  // Update Firestore on successful payment
  useEffect(() => {
    if (decision === 'ACCEPT' && auth.currentUser) {
      const updateFirestore = async () => {
        try {
          const userId = auth.currentUser.uid;
          const paymentId = `PAY-${Date.now()}`;
          const invoiceId = `INV-${Date.now()}`;
          const paymentDate = new Date().toISOString();
          const plan = referenceNumber.includes('monthly') ? 'monthly' : 'yearly';
          const amount = plan === 'monthly' ? 300 : 3000;
          const subscriptionEnd = new Date();
          subscriptionEnd.setDate(plan === 'monthly' ? subscriptionEnd.getDate() + 30 : subscriptionEnd.getDate() + 365);

          // Update user subscription
          await setDoc(
            doc(db, "users", userId),
            {
              status: 'active',
              plan,
              subscriptionStart: paymentDate,
              subscriptionEnd: subscriptionEnd.toISOString(),
            },
            { merge: true }
          );

          // Record payment
          await setDoc(doc(db, `users/${userId}/payments`, paymentId), {
            paymentId,
            userId,
            amount,
            plan,
            date: paymentDate,
            status: 'active',
            invoiceId,
            transactionId,
          });
        } catch (err) {
          console.error("Error updating Firestore:", err);
          setModal({
            visible: true,
            message: "Payment successful, but failed to update subscription. Contact support.",
            isSuccess: false,
          });
        }
      };
      updateFirestore();
    }
  }, [decision, transactionId, referenceNumber]);

  const closeModal = () => {
    setModal({ ...modal, visible: false });
    navigate('/');
  };

  return (
    <Container sx={{ mt: 10, mb: 10, textAlign: 'center' }}>
      <ModernDialog open={modal.visible} onClose={closeModal}>
        <DialogTitle sx={{ background: "#4caf50", color: "white", fontWeight: 700 }}>
          {modal.isSuccess ? 'Success' : 'Error'}
        </DialogTitle>
        <ModernDialogContent>
          <Typography sx={{ mb: 2 }}>{modal.message}</Typography>
          {modal.isSuccess ? (
            <>
              <Typography sx={{ mb: 1 }}>Transaction ID: {transactionId}</Typography>
              <Typography>Reference Number: {referenceNumber}</Typography>
            </>
          ) : (
            <Typography>Reason Code: {reasonCode}</Typography>
          )}
        </ModernDialogContent>
        <DialogActions sx={{ p: 3 }}>
          <SelectButton onClick={closeModal}>
            {modal.isSuccess ? 'Go to Home' : 'Close'}
          </SelectButton>
        </DialogActions>
      </ModernDialog>
    </Container>
  );
};

// Updated App component to include routing
const App = () => {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<Subscription />} />
        <Route path="/response" element={<ResponsePage />} />
      </Routes>
    </Router>
  );
};

export default App;